import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle } from 'lucide-react';
import { useChat } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/AuthContext';
import { getI18n } from '@/lib/i18n';

/**
 * Компактная кнопка «Чат» для использования в строке поставщика / в карточке тендера.
 * Открывает плавающий виджет с указанным диалогом.
 */
export function ChatButton({ tenderId, supplierEmail, label, labelKey, size = 'xs', className = '' }) {
  const { user } = useAuth();
  const { t } = getI18n(user);
  const { openThread, threads } = useChat();
  const th = threads.find(x => x.tender_id === tenderId && x.supplier_email === (supplierEmail || '').toLowerCase());
  const unread = th?.unread_count || 0;
  const buttonLabel = label || (labelKey ? t(labelKey) : t('chat.btn.short'));

  const baseCls = size === 'xs'
    ? 'h-7 px-2 text-[11px] rounded-sm'
    : 'h-8 px-2.5 text-xs rounded-sm';

  return (
    <Button
      type="button"
      variant="outline"
      onClick={(e) => { e.stopPropagation(); openThread(tenderId, supplierEmail); }}
      data-testid={`chat-open-btn-${tenderId}-${(supplierEmail || '').toLowerCase()}`}
      className={`${baseCls} relative border-[#0046AD]/30 text-[#0046AD] hover:bg-blue-50 ${className}`}
    >
      <MessageCircle className={size === 'xs' ? 'w-3 h-3 mr-1' : 'w-3.5 h-3.5 mr-1'} />
      {buttonLabel}
      {unread > 0 && (
        <Badge className="absolute -top-1.5 -right-1.5 bg-rose-500 hover:bg-rose-500 text-white text-[9px] h-4 min-w-4 px-1 rounded-full">
          {unread}
        </Badge>
      )}
    </Button>
  );
}

/**
 * Кнопка-индикатор для шапки приложения (Bell-icon analog).
 * Показывает суммарное число непрочитанных и при клике открывает чат.
 */
export function ChatHeaderButton() {
  const { unread, toggleWidget } = useChat();
  return (
    <button
      type="button"
      onClick={toggleWidget}
      data-testid="chat-header-btn"
      className="relative w-9 h-9 rounded-sm hover:bg-slate-100 flex items-center justify-center text-slate-600 transition"
      title="Чаты"
    >
      <MessageCircle className="w-4 h-4" />
      {unread > 0 && (
        <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[9px] font-bold rounded-full"
          data-testid="chat-header-unread">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}
